import { TemplateType } from "@xapp/ovai-lib";
import { Role } from "@xapp/stentor-api-client/dist/models/Roles";
import { hasOrgRole, isAdmin } from "auth";
import { StentorAdmin, STENTOR_ADMIN } from "auth/modules/roles";
import { UserProfile } from "auth/modules/state";
import { BetaMap, ClientApp } from "models";
import { existsAndNotEmpty } from "stentor-utils";
import { NavItem, SecondaryNavItem } from "./SecondaryNavView";

/**
 * Determines if the navigation item has any restrictions on if it is displayed or not.
 *
 * @export
 * @param {NavItem} navItem
 * @returns {boolean}
 */
export function hasRestrictions(navItem: NavItem): boolean {
    return (
        existsAndNotEmpty(navItem.showWithBetaFlags) ||
        existsAndNotEmpty(navItem.showWithRoles) ||
        existsAndNotEmpty(navItem.showWithTemplateTypes) ||
        existsAndNotEmpty(navItem.doNotShowWithTemplateTypes)
    );
}

/**
 * Determines if a NavItem should be hidden due to role restriction purposes.
 *
 * @export
 * @param {((Role | StentorAdmin)[])} allowedRoles
 * @param {UserProfile} user
 * @param {Organization} org
 * @param {boolean} asUser
 * @returns {boolean}
 */
export function showNavItemForUser(
    allowedRoles: (Role | StentorAdmin)[],
    user: UserProfile,
    organizationId: string,
    asUser: boolean
): boolean {
    // if it lacks any restrictions
    // or the user is an Admin
    // and the 'asUser' flag is not set
    if (!Array.isArray(allowedRoles) || (isAdmin(user) && !asUser)) {
        // show it
        return true;
    }

    // if item is flagged adminOnly and the user is an admin
    // The asUser makes this different from the one above.
    if (allowedRoles.includes(STENTOR_ADMIN) && isAdmin(user)) {
        // pass the test
        return true;
    }

    if (organizationId) {
        let hasRole = false;
        allowedRoles.forEach((allowedOrgRole) => {
            // if the user does have the proper role for the given organization
            if (hasOrgRole(organizationId, allowedOrgRole as Role, user)) {
                // do not hide it
                hasRole = true;
            }
        });

        if (hasRole) {
            return true;
        }
    }
    return false;
}

/**
 * Show if the template type is included in the array of template types.
 *
 * @export
 * @param {string} templateType
 * @param {TemplateType[]} templateTypes
 * @returns {boolean}
 */
export function showNavItemForTemplateType(templateType: string, templateTypes: (TemplateType | string)[]): boolean {
    if (
        existsAndNotEmpty(templateTypes) &&
        templateTypes.find((allowedTemplateType: TemplateType) => templateType === allowedTemplateType)
    ) {
        return true;
    }

    return false;
}

/**
 * Decide to show the item for the App.
 *
 * @export
 * @param {TemplateType[]} templateTypes
 * @param {string[]} betaFlags
 * @returns {boolean}
 */
export function showNavItemForBetas(betas: BetaMap, betaFlags: string[]): boolean {
    // Fast fail
    if (typeof betas !== "object") {
        return false;
    }

    return betaFlags.some((flag: string) => {
        return betas[flag] === true;
    });
}

/**
 * Returns TRUE meaning the template type was found in the disallowed.
 *
 * @param templateType
 * @param templateTypes
 * @returns
 */
export function doNotShowNavItemForTemplateType(templateType: string, templateTypes: (TemplateType | string)[]): boolean {

    if (existsAndNotEmpty(templateTypes)) {

        const foundInDisallowed = templateTypes.find((disallowedTemplateType: TemplateType) => templateType === disallowedTemplateType);

        if (foundInDisallowed) {
            return true;
        }
    }

    return false;
}

/**
 * Determination if the nav item is show or not based on user, app, & org information.
 *
 * If showingForAdmin is true the only reason why the item is being show is because the user is an admin, this is helpful
 * to communicate to the admin that this is the reason why is is being shown.
 */
export function showNavItem(
    item: NavItem,
    user: UserProfile,
    organizationId: string,
    app?: Pick<ClientApp, "templateType" | "beta" | "appId">,
    asUser = false
): { show: boolean, showingForAdmin?: boolean } {
    let show = true;
    let showingForAdmin = false;

    // Any restrictions at all limits visibility.
    if (hasRestrictions(item)) {
        // If there are any restrictions at all
        // then the user needs to prove they can see the route
        show = false;

        let showForTemplateAndBeta = true;

        let showForRoles = false;
        if (existsAndNotEmpty(item.showWithRoles)) {
            showForRoles = showNavItemForUser(item.showWithRoles, user, organizationId, asUser);
            if (showForRoles) {
                showingForAdmin = item.showWithRoles.includes("stentor_admin");
            }
        }

        let showForTemplateType = false;
        if (existsAndNotEmpty(item.showWithTemplateTypes)) {
            // We need an app!  Not always available
            if (app) {
                showForTemplateType = showNavItemForTemplateType(app.templateType, item.showWithTemplateTypes);

                if (showForTemplateType) {
                    // this one takes precedence
                    showingForAdmin = false;
                }
            }
        }

        let showForBetaFlags = false;
        if (existsAndNotEmpty(item.showWithBetaFlags)) {
            if (app?.beta) {
                showForBetaFlags = showNavItemForBetas(app.beta, item.showWithBetaFlags);
                // don't show when template exist but beta flag not enabled
                if (showForTemplateType && !showForBetaFlags) {
                    showForTemplateAndBeta = false;
                }
            }
        }

        let doNotShow = false;
        if (existsAndNotEmpty(item.doNotShowWithTemplateTypes)) {
            if (app) {
                doNotShow = doNotShowNavItemForTemplateType(app.templateType, item.doNotShowWithTemplateTypes);
            }
        }

        if (doNotShow) {
            // if do not, make sure we aren't being overridden by showForRoles or showForBetaFlags
            if (showForRoles || showForBetaFlags) {
                show = true;
            } else {
                show = false;
            }
        } else if (existsAndNotEmpty(item.doNotShowWithTemplateTypes)) {
            // if doNotShow was false and we have items, then we passed!
            show = true;
        } else {
            // check everything else
            show = (showForTemplateType || showForRoles || showForBetaFlags) && showForTemplateAndBeta;
        }
    }

    return { show, showingForAdmin };
}

/**
 * Check to see if the provided app has the beta feature enabled.
 *
 * @export
 * @param {ClientApp} app
 * @param {string[]} [betaFlags=[]]
 * @returns {boolean}
 */
export function appHasBetaFeatureEnabled(app: Pick<ClientApp, "beta">, betaFlags: string[] = []): boolean {
    if (!app || !app.beta || !betaFlags || betaFlags.length === 0) {
        return false;
    }

    return betaFlags.some((flag: string) => {
        return app.beta[flag] === true;
    });
}

export function isActive(itemUrl: string, location: string): boolean {
    return itemUrl === location;
}
/**
 * Scans the children and determines whether a MenuItem should be flagged as active
 *
 * @export
 * @param {MenuItem} navItem
 * @param {string} location
 * @returns {boolean}
 */
export function hasActiveChildren(navItem: SecondaryNavItem, location: string): boolean {
    let hasActiveChildren = false;

    if (isActive(navItem.path, location)) {
        return true;
    }

    if (navItem.submenuItems && navItem.submenuItems.length) {
        if (navItem.submenuItems.find((item) => isActive(item.path, location))) {
            hasActiveChildren = true;
        }
    }

    return hasActiveChildren;
}
